import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import LocalizedLink from 'helpers/LocalizeLink';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ApiService from "services/api/api.service";
import { CartItemRemove, CartItemSelect } from 'helpers/cart/CartItem';
import { CheckoutButton } from 'helpers/cart/AddToCart';
import { setCheckoutCart, setCart } from 'state/actions';
import { Modal } from 'antd';
import { getLocalePrefix, STRATEGIES_LIBRARY_PATH } from "helpers/util";

const EmptyCart = () => {
    const { t } = useTranslation()
    return (
        <div className="text-center text-empty text-semibold w-100">{t('The cart is empty')}</div>
    )
}


const FullCartItemInfo = ({ item, index }) => {
    const { t } = useTranslation()
    return (
        <div className="text-sm-center w-100">
            <h3 className="mb-2 text-justify text-sm-center">
                <LocalizedLink to={`strategy/${item?.id}`} dti={"cart-item-name-" + index} className="text-darkblue text-sm-big text-bold">{item?.name}</LocalizedLink>
            </h3>
            {item.type === "strategy" ? <p data-test-id={"cart-item-sales-" + index} className="text-small text-bold">{item?.sales} {t('labels.sold_by')} <span>{item?.seller_name}</span></p> : null}
        </div>
    )
}

const FullCartItemPrice = ({ item }) => {
    const currency = useSelector(store => store.currency)
    return (item?.disabled === true) ? null : <h3 className="cart-item-price text-sm-big text-center text-green ml-auto">{currency.symbol}{item.price}</h3>
}

const compareItemsWithChecked = (arr1, arr2) => {
    return (arr1.length === arr2.length && JSON.stringify([...arr1].sort((a, b) => a - b)) === JSON.stringify([...arr2].sort((a, b) => a - b)));
}

const FullCartSelectAll = () => {

    const { t } = useTranslation()
    const dispatch = useDispatch()
    const checkoutCart = useSelector(store => store.checkout_cart)

    const checked = Object.keys(checkoutCart).filter(key =>
        !checkoutCart[key]?.disabled && !!checkoutCart[key]?.checked
    )
    const items = Object.keys(checkoutCart).filter(key =>
        !checkoutCart[key]?.disabled && !checkoutCart[key]?.updated
    )
    const selectedAll = compareItemsWithChecked(items, checked)

    return (
        <div className="input-checkbox d-flex mb-3 mb-sm-0 ml-sm-3">
            <label>
                <input
                    data-test-id="cart-check-all"
                    id="checkAll"
                    name="check-all"
                    type="checkbox"
                    checked={selectedAll}
                    onChange={() => {
                        const updatedCart = {};
                        Object.keys(checkoutCart).forEach(key => {
                            const cartItem = checkoutCart[key];
                            const isNotUpdated = (
                                cartItem.updated === undefined ||
                                cartItem.updated === false ||
                                cartItem.updated === null
                            );
                            if (isNotUpdated) {
                                updatedCart[key] = { ...cartItem, checked: !selectedAll };
                            } else {
                                updatedCart[key] = cartItem;
                            }
                        });

                        dispatch(setCheckoutCart(updatedCart));
                    }} />
                <i data-test-id="cart-check-all-click"></i>
            </label>
            <label htmlFor="checkAll" className="text-small text-justify ml-3">
                {t('Select/Unselect All Available')}
            </label>
        </div>
    )
}

const FullCart = ({ checkCart }) => {
    const { t } = useTranslation()
    const checkoutCart = useSelector(store => store.checkout_cart)

    return (
        <>
            <div className="cart col-12 col-md-8 col-lg-9 mx-auto mx-md-0">
                <div className="cart-options d-flex justify-content-between align-items-start align-items-sm-center flex-column-reverse flex-sm-row px-3 w-100">
                    <FullCartSelectAll />
                    <LocalizedLink to={getLocalePrefix() + STRATEGIES_LIBRARY_PATH} dti="cart-backtomrkt-btn" className="text-lightgrey text-small text-semibold mb-3 mb-sm-0 mr-sm-3">{t('buttons.btn_back_marketplace')}</LocalizedLink>
                </div>
                {Object.keys(checkoutCart).map((key, index) => {
                    let cart_item = checkoutCart[key]
                    return (cart_item?.disabled === true) ? null :
                        (
                            <div key={key} className="cart-item mb-3 px-3">
                                <div className="cart-item-head d-flex justify-content-between align-items-center py-2">
                                    {cart_item?.updated === true ? null : <CartItemSelect item={cart_item} index={index} />}
                                    <CartItemRemove item={cart_item} index={index} />
                                </div>
                                <div className="separator gradient"></div>
                                <div className="cart-item-body d-flex justify-content-start align-items-center py-3">
                                    <div className="d-none d-lg-block cart-item-image w-100 mr-5"></div>
                                    <FullCartItemInfo item={cart_item} index={index} />
                                </div>
                                <div className="separator gradient"></div>
                                <div className="cart-item-footer d-flex justify-content-end align-items-center py-1">
                                    {cart_item.updated ? <p data-test-id={"cart-item-updated-" + index} className="text-small text-red text-semibold">{t('labels.strategy_updated')}</p> : null}
                                    <FullCartItemPrice item={cart_item} />
                                </div>
                            </div>
                        )
                })}
            </div>
            <Checkout checkCart={checkCart} />
        </>
    )
}

const Checkout = ({ checkCart }) => {
    const { t } = useTranslation()
    const currency = useSelector(store => store.currency)
    const checkoutCart = useSelector(store => store.checkout_cart)

    const totalPrice =
        Object.keys(checkoutCart).reduce((accumulator, currentValue) => {
            return checkoutCart[currentValue].checked ?
                accumulator + (checkoutCart[currentValue].price > 0 ? (checkoutCart[currentValue].price * checkoutCart[currentValue].quantity) : 0) :
                accumulator
        }, 0).toFixed(2)

    const checkoutValues = Object.values(checkoutCart);

    const selectedItemsCount = checkoutValues.filter(item => item?.checked).length;

    const allItemsUpdated = checkoutValues.length > 0 &&
        checkoutValues.every(item => item?.updated || item?.disabled);

    return (
        <div className="price col-12 col-md-4 col-lg-3 mx-auto mx-md-0">
            <div className="price-total text-sm-center px-3 py-2 mb-4">
                <h3 className="pb-3">{t('labels.total_price')}:</h3>
                <h3 data-test-id="cart-total-price" className="text-green mt-3">{currency.symbol}{totalPrice}</h3>
            </div>
            {
                selectedItemsCount ?
                    <CheckoutButton callbefore={checkCart} type="submit" />
                    :
                    allItemsUpdated ?
                        <button type="button" className="btn disabled h-42 px-12 w-100" disabled>
                            {t('buttons.btn_proced_to_checkout')}
                        </button>
                        :
                        null
            }
        </div>
    )
}

const CartWidget = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const cart = useSelector(store => store.cart)
    const checkoutCart = useSelector(store => store.checkout_cart)

    const prevCartIdsRef = useRef(null);
    const popupedIdsRef = useRef(new Set());

    const actualize_cart = useCallback(async () => {
        if (cart.length === 0) return null;

        const ids = cart.map(item => item.id).join('|');

        return await ApiService.getData("/content/cart?ids=" + ids)
            .then((res) => {
                let tempCart = {}

                cart.forEach((item) => {
                    const result_item = res.data.find((response_item) => response_item.id === item.id);

                    let updatedItem;
                    if (!result_item) {
                        updatedItem = { ...item, disabled: true };
                    } else if (item.options.EditableOnMarketPlace !== result_item.options.EditableOnMarketPlace || item.price !== result_item.price) {
                        updatedItem = { ...item, updated: true, checked: false, disabled: false };
                    } else {
                        updatedItem = { ...item, updated: false, disabled: false };
                    }

                    if (tempCart[updatedItem.id]) {
                        tempCart[updatedItem.id] = { ...tempCart[updatedItem.id], quantity: tempCart[updatedItem.id].quantity + 1 };
                    } else {
                        const prevChecked = checkoutCart[updatedItem.id] ? checkoutCart[updatedItem.id].checked : false;
                        tempCart[updatedItem.id] = {
                            ...updatedItem,
                            quantity: 1,
                            checked: updatedItem.updated ? false : prevChecked
                        };
                    }
                    return tempCart;
                })
                dispatch(setCheckoutCart(tempCart));
                return tempCart;
            })
            .catch(() => null);
    }, [cart, checkoutCart, dispatch]);

    const checkCart = useCallback(async (callbacknext = null) => {
        const tempCart = await actualize_cart();
        if (!tempCart) return;

        const tempValues = Object.values(tempCart);
        const hasUpdatedItem = tempValues.some(item => item?.updated);
        const hasDisabledItem = tempValues.some(item => item?.disabled);
        const hasPopupedItem = tempValues.some(item => item?.updated && !popupedIdsRef.current.has(item.id));

        if (hasPopupedItem || hasDisabledItem) {
            const nonDisabledIds = new Set(tempValues.filter(i => !i.disabled).map(i => i.id));
            const cartItemsFilteredDisabled = cart.filter(cart_item => nonDisabledIds.has(cart_item.id));

            const queryParams = new URLSearchParams(window.location.search);
            if (queryParams.get('nopopup') !== '1') {

                if (hasUpdatedItem) {
                    Modal.warning({
                        title: t("Warning!"),
                        content: (<p>{t('errors.err_product_updated')}</p>),
                        okText: "Ok",
                        okButtonProps: { 'data-test-id': 'update-modal-ok' },
                        wrapProps: { 'data-test-id': 'update-modal-wr' }
                    });
                    tempValues.forEach(item => {
                        if (item.updated) popupedIdsRef.current.add(item.id);
                    });
                }
                if (hasDisabledItem) {
                    Modal.warning({
                        title: t("Warning!"),
                        content: (<p>{t('errors.err_product_unposted')}</p>),
                        okText: "Ok",
                        okButtonProps: { 'data-test-id': 'unpost-modal-ok' },
                        wrapProps: { 'data-test-id': 'unpost-modal-wr' }
                    });
                    dispatch(setCart(cartItemsFilteredDisabled));
                }
            }
        } else {
            if (callbacknext !== null) callbacknext();
        }
    }, [actualize_cart, cart, dispatch, t]);

    useEffect(() => {
        const cartIds = cart.map(item => item.id).join('|');
        if (prevCartIdsRef.current === cartIds) return;
        prevCartIdsRef.current = cartIds;
        checkCart();
    }, [cart, checkCart])

    return (
        <div className="container-xl row align-items-start mx-auto p-0 mb-32">
            {
                cart.length === 0 ?
                    <EmptyCart /> :
                    <FullCart checkCart={checkCart} />
            }
        </div >
    )
}

CartWidget.propTypes = {
    title: PropTypes.string,
    descriptions: PropTypes.string,
    additional_text: PropTypes.string,
    button_text: PropTypes.string,
    path: PropTypes.string,
    img: PropTypes.string,
    position: PropTypes.number,
    widget: PropTypes.string,
    className: PropTypes.string,
    items: PropTypes.array,
    tickets: PropTypes.string,
    seller_name: PropTypes.string
}

export default CartWidget;
